import React, { useState, useEffect } from "react";
import { 
  Tabs, Form, Input, Button, Select, message, Table, Tag, Modal, 
  Card, Row, Col, Statistic, Empty, Badge, Space, Tooltip
} from "antd";
import { 
  BugOutlined, MessageOutlined, PlusOutlined, 
  ExclamationCircleOutlined, CheckCircleOutlined, 
  ClockCircleOutlined, SyncOutlined, LikeOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const ISSUE_CATEGORIES = [
  "Login / Authentication",
  "Dashboard / Navigation",
  "Job Posting",
  "Talent Search",
  "Profile Management",
  "Payments / Billing",
  "Timesheet",
  "Reports",
  "Performance",
  "Other"
];

const FEEDBACK_CATEGORIES = [
  { value: "feature_request", label: "Feature Request" },
  { value: "performance", label: "Performance Improvement" },
  { value: "ui_ux", label: "UI/UX Enhancement" },
  { value: "documentation", label: "Documentation" },
  { value: "integration", label: "Integration Suggestion" },
  { value: "general", label: "General Feedback" },
];

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;

  .header-actions {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
  }

  .stats-row {
    margin-bottom: 24px;
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .ant-statistic-title {
      color: #64748b;
      font-size: 13px;
    }

    .ant-statistic-content-value {
      color: #1e293b;
      font-weight: 600;
    }
  }

  .main-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .ant-tabs-nav {
    margin: 0;
    padding: 0 24px;
    background: #fafafa;
    border-bottom: 1px solid #e8e8e8;
  }

  .tab-content {
    padding: 24px;
  }

  .action-btn {
    background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
    border: none;
    height: 40px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 600;
    color: white;
  }

  .action-btn:hover {
    background: linear-gradient(135deg, #00b894 0%, #01d9a9 100%);
    color: white;
  }

  .feedback-btn {
    background: linear-gradient(135deg, #014c75 0%, #0369a1 100%);
    border: none;
    height: 40px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 600;
    color: white;
  }

  .feedback-btn:hover {
    background: linear-gradient(135deg, #0369a1 0%, #014c75 100%);
    color: white;
  }
`;

const IssuesFeedback = () => {
  const [activeTab, setActiveTab] = useState("issues");
  const [issueForm] = Form.useForm();
  const [feedbackForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({ issues: { total: 0, open: 0, resolved: 0 }, feedback: { total: 0, acknowledged: 0, pending: 0 } });
  const [detailModal, setDetailModal] = useState({ visible: false, record: null });
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRecords("issue");
    fetchRecords("feedback");
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ISSUE_FEEDBACK_STATS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRecords = async (type) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ISSUE_FEEDBACK_LIST,
        { type, limit: 50 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        if (type === "issue") {
          setIssues(response.data.records);
        } else {
          setFeedback(response.data.records);
        }
      }
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIssue = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.CREATE_ISSUE_FEEDBACK,
        { ...values, type: "issue" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Issue submitted successfully!");
        issueForm.resetFields();
        setIssueModalVisible(false);
        fetchStats();
        fetchRecords("issue");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to submit issue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFeedback = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.CREATE_ISSUE_FEEDBACK,
        { ...values, type: "feedback" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Feedback submitted successfully!");
        feedbackForm.resetFields();
        setFeedbackModalVisible(false);
        fetchStats();
        fetchRecords("feedback");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledge = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_CONST.ACKNOWLEDGE_ISSUE_FEEDBACK.replace(':id', record.id)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Acknowledged successfully!");
        setDetailModal({ visible: false, record: null });
        fetchStats();
        if (record.type === "issue") {
          fetchRecords("issue");
        } else {
          fetchRecords("feedback");
        }
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to acknowledge");
    }
  };

  const getStatusTag = (status) => {
    const config = {
      open: { color: "orange", icon: <ClockCircleOutlined /> },
      in_progress: { color: "blue", icon: <SyncOutlined spin /> },
      resolved: { color: "green", icon: <CheckCircleOutlined /> },
      closed: { color: "default", icon: <CheckCircleOutlined /> },
      acknowledged: { color: "cyan", icon: <LikeOutlined /> },
    };
    const { color, icon } = config[status] || { color: "default" };
    return <Tag color={color} icon={icon}>{status?.replace("_", " ").toUpperCase()}</Tag>;
  };

  const getPriorityTag = (priority) => {
    const colors = {
      low: "default",
      medium: "blue",
      high: "orange",
      critical: "red",
    };
    return <Tag color={colors[priority] || "default"}>{priority?.toUpperCase()}</Tag>;
  };

  const getFeedbackCategoryLabel = (value) => {
    const cat = FEEDBACK_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const issueColumns = [
    { 
      title: "ID", 
      dataIndex: "id", 
      key: "id",
      width: 140,
      render: (id) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>
    },
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    { title: "Category", dataIndex: "category", key: "category", width: 150 },
    { 
      title: "Priority", 
      dataIndex: "priority", 
      key: "priority",
      width: 100,
      render: (p) => getPriorityTag(p)
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      width: 130,
      render: (s) => getStatusTag(s)
    },
    { 
      title: "Created", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 120,
      render: (d) => dayjs(d).format("MMM DD, YYYY")
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => setDetailModal({ visible: true, record })}>
            View
          </Button>
          {(record.status === "resolved" || record.status === "closed") && record.status !== "acknowledged" && (
            <Tooltip title="Acknowledge Resolution">
              <Button 
                type="link" 
                size="small" 
                style={{ color: "#01d9a9" }}
                onClick={() => handleAcknowledge(record)}
              >
                Acknowledge
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const feedbackColumns = [
    { 
      title: "ID", 
      dataIndex: "id", 
      key: "id",
      width: 140,
      render: (id) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>
    },
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    { 
      title: "Category", 
      dataIndex: "feedbackCategory", 
      key: "feedbackCategory",
      width: 180,
      render: (c) => <Tag color="purple">{getFeedbackCategoryLabel(c)}</Tag>
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      width: 130,
      render: (s) => getStatusTag(s)
    },
    { 
      title: "Created", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 120,
      render: (d) => dayjs(d).format("MMM DD, YYYY")
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setDetailModal({ visible: true, record })}>
          View
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: "issues",
      label: (
        <span>
          <BugOutlined /> My Issues
          {stats.issues.open > 0 && <Badge count={stats.issues.open} style={{ marginLeft: 8 }} />}
        </span>
      ),
      children: (
        <div className="tab-content">
          {issues.length === 0 ? (
            <Empty 
              description="You haven't reported any issues yet"
              style={{ padding: "60px 0" }}
            >
              <Button type="primary" className="action-btn" icon={<PlusOutlined />} onClick={() => setIssueModalVisible(true)}>
                Report Your First Issue
              </Button>
            </Empty>
          ) : (
            <Table 
              columns={issueColumns} 
              dataSource={issues} 
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="middle"
            />
          )}
        </div>
      ),
    },
    {
      key: "feedback",
      label: (
        <span>
          <MessageOutlined /> My Feedback
          {stats.feedback.pending > 0 && <Badge count={stats.feedback.pending} style={{ marginLeft: 8, backgroundColor: "#014c75" }} />}
        </span>
      ),
      children: (
        <div className="tab-content">
          {feedback.length === 0 ? (
            <Empty 
              description="You haven't provided any feedback yet"
              style={{ padding: "60px 0" }}
            >
              <Button type="primary" className="feedback-btn" icon={<PlusOutlined />} onClick={() => setFeedbackModalVisible(true)}>
                Share Your First Feedback
              </Button>
            </Empty>
          ) : (
            <Table 
              columns={feedbackColumns} 
              dataSource={feedback} 
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="middle"
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <PageWrapper>
      <PageHeader>
        <h1>Issues & Feedback</h1>
        <p>Report issues you encounter or share suggestions to help us improve</p>
      </PageHeader>
      <Row gutter={16} className="stats-row">
        <Col span={6}>
          <Card className="stat-card">
            <Statistic 
              title="Total Issues" 
              value={stats.issues.total} 
              prefix={<BugOutlined style={{ color: "#f59e0b" }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic 
              title="Open Issues" 
              value={stats.issues.open} 
              prefix={<ClockCircleOutlined style={{ color: "#3b82f6" }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic 
              title="Resolved Issues" 
              value={stats.issues.resolved} 
              prefix={<CheckCircleOutlined style={{ color: "#10b981" }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic 
              title="Total Feedback" 
              value={stats.feedback.total} 
              prefix={<MessageOutlined style={{ color: "#014c75" }} />}
            />
          </Card>
        </Col>
      </Row>

      <Card className="main-card">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={
            <Space>
              <Button 
                type="primary" 
                className="action-btn" 
                icon={<BugOutlined />} 
                onClick={() => setIssueModalVisible(true)}
              >
                Report Issue
              </Button>
              <Button 
                type="primary" 
                className="feedback-btn" 
                icon={<MessageOutlined />} 
                onClick={() => setFeedbackModalVisible(true)}
              >
                Provide Feedback
              </Button>
            </Space>
          }
        />
      </Card>

      <Modal
        title={<><BugOutlined style={{ marginRight: 8 }} /> Report an Issue</>}
        open={issueModalVisible}
        onCancel={() => { setIssueModalVisible(false); issueForm.resetFields(); }}
        footer={null}
        width={600}
      >
        <p style={{ color: "#64748b", marginBottom: 24 }}>
          Having trouble with something? Let us know and we'll look into it right away.
        </p>
        <Form form={issueForm} layout="vertical" onFinish={handleSubmitIssue}>
          <Form.Item
            name="category"
            label="Issue Category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select the area where you encountered the issue">
              {ISSUE_CATEGORIES.map((cat) => (
                <Option key={cat} value={cat}>{cat}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="title"
            label="Issue Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="Brief description of the issue" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please describe the issue" }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Please provide details about the issue, including steps to reproduce if possible"
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            initialValue="medium"
          >
            <Select>
              <Option value="low">Low - Minor inconvenience</Option>
              <Option value="medium">Medium - Affecting my work</Option>
              <Option value="high">High - Blocking my work</Option>
              <Option value="critical">Critical - System down or data loss</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setIssueModalVisible(false); issueForm.resetFields(); }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting}
                style={{ background: "linear-gradient(135deg, #01d9a9 0%, #00b894 100%)", border: "none" }}
              >
                Submit Issue
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<><MessageOutlined style={{ marginRight: 8 }} /> Provide Feedback</>}
        open={feedbackModalVisible}
        onCancel={() => { setFeedbackModalVisible(false); feedbackForm.resetFields(); }}
        footer={null}
        width={600}
      >
        <p style={{ color: "#64748b", marginBottom: 24 }}>
          Your feedback helps us improve. Share your ideas, suggestions, or thoughts with us.
        </p>
        <Form form={feedbackForm} layout="vertical" onFinish={handleSubmitFeedback}>
          <Form.Item
            name="feedbackCategory"
            label="Feedback Type"
            rules={[{ required: true, message: "Please select a feedback type" }]}
          >
            <Select placeholder="What kind of feedback is this?">
              {FEEDBACK_CATEGORIES.map((cat) => (
                <Option key={cat.value} value={cat.value}>{cat.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="Brief summary of your feedback" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please describe your feedback" }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Share your thoughts, ideas, or suggestions in detail"
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setFeedbackModalVisible(false); feedbackForm.resetFields(); }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting}
                style={{ background: "linear-gradient(135deg, #014c75 0%, #0369a1 100%)", border: "none" }}
              >
                Submit Feedback
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={detailModal.record?.type === "issue" ? "Issue Details" : "Feedback Details"}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, record: null })}
        footer={
          detailModal.record?.type === "issue" && 
          (detailModal.record?.status === "resolved" || detailModal.record?.status === "closed") && 
          detailModal.record?.status !== "acknowledged" ? (
            <Space>
              <Button onClick={() => setDetailModal({ visible: false, record: null })}>Close</Button>
              <Button 
                type="primary" 
                icon={<LikeOutlined />}
                onClick={() => handleAcknowledge(detailModal.record)}
                style={{ background: "#01d9a9", border: "none" }}
              >
                Acknowledge Resolution
              </Button>
            </Space>
          ) : (
            <Button onClick={() => setDetailModal({ visible: false, record: null })}>Close</Button>
          )
        }
        width={600}
      >
        {detailModal.record && (
          <div style={{ lineHeight: 2 }}>
            <p><strong>ID:</strong> <code>{detailModal.record.id}</code></p>
            <p><strong>Title:</strong> {detailModal.record.title}</p>
            {detailModal.record.type === "issue" ? (
              <>
                <p><strong>Category:</strong> {detailModal.record.category}</p>
                <p><strong>Priority:</strong> {getPriorityTag(detailModal.record.priority)}</p>
              </>
            ) : (
              <p><strong>Type:</strong> <Tag color="purple">{getFeedbackCategoryLabel(detailModal.record.feedbackCategory)}</Tag></p>
            )}
            <p><strong>Status:</strong> {getStatusTag(detailModal.record.status)}</p>
            <p><strong>Submitted:</strong> {dayjs(detailModal.record.createdAt).format("MMMM DD, YYYY [at] HH:mm")}</p>
            <div style={{ marginTop: 16, padding: 16, background: "#f5f7fa", borderRadius: 8 }}>
              <strong>Description:</strong>
              <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{detailModal.record.description}</p>
            </div>
            {detailModal.record.resolution && (
              <div style={{ marginTop: 16, padding: 16, background: "#ecfdf5", borderRadius: 8 }}>
                <strong style={{ color: "#059669" }}>Resolution:</strong>
                <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{detailModal.record.resolution}</p>
              </div>
            )}
            {detailModal.record.adminNotes && (
              <div style={{ marginTop: 16, padding: 16, background: "#fff7ed", borderRadius: 8 }}>
                <strong style={{ color: "#c2410c" }}>Admin Notes:</strong>
                <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{detailModal.record.adminNotes}</p>
              </div>
            )}
            {detailModal.record.acknowledgedAt && (
              <p style={{ marginTop: 16, color: "#059669" }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Acknowledged on {dayjs(detailModal.record.acknowledgedAt).format("MMMM DD, YYYY [at] HH:mm")}
                {detailModal.record.acknowledgedByName && ` by ${detailModal.record.acknowledgedByName}`}
              </p>
            )}
          </div>
        )}
      </Modal>
    </PageWrapper>
  );
};

export default IssuesFeedback;
